import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';

import { boatActions } from '@/entities/boat';
import { userActions } from '@/entities/user';
import { baseQuery } from '@/shared/api/common.api';
import { AppStorage } from '@/shared/constants/app-storage.constant';
import { StatusCodeError } from '@/shared/constants/status-code-error.constant';

// TODO: use Yachting for localhost
// const hostMapping: { [key in 'cloud' | 'yachts' | 'localhost']: string } = {
// 	cloud: 'Industrial',
// 	yachts: 'Yachting',
// 	localhost: 'Yachting',
// };

let refreshPromise: Promise<{ access_token: string; refresh_token: string } | null> | null = null;

export const baseQueryWithReauth: (apiUrl: string) => BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> =
	(apiUrl: string) => async (args, api, extraOptions) => {
		const isUserApiRequest = apiUrl === USER_API_URL;
		const isUserApiV2 = AUTH_V2_ENABLED && isUserApiRequest;

		// TODO: in progress
		// const hostMappingKey = Object.keys(hostMapping).find((key) => window.location.host.includes(key));
		// const sector = hostMappingKey && hostMapping[hostMappingKey as keyof typeof hostMapping];
		const sector = 'Yachting';

		const buildRequestArgs = (): string | FetchArgs => {
			if (!args || typeof args !== 'object') {
				return args;
			}

			const requestArgs: FetchArgs = { ...args };

			if (!sector || isUserApiV2) {
				return requestArgs;
			}

			const method = requestArgs.method?.toUpperCase();

			if (['GET', 'DELETE'].includes(method as string)) {
				requestArgs.params = { ...(requestArgs.params as Record<string, unknown>), sector };
				return requestArgs;
			}

			if (requestArgs.body instanceof FormData) {
				const formData = new FormData();
				requestArgs.body.forEach((value, key) => {
					formData.append(key, value);
				});
				formData.set('sector', sector);
				requestArgs.body = formData;
				return requestArgs;
			}

			if (requestArgs.body) {
				requestArgs.body = { ...(requestArgs.body as Record<string, unknown>), sector };
			}

			return requestArgs;
		};

		const result = await baseQuery(apiUrl)(buildRequestArgs(), api, extraOptions);
		const error = result?.error as { status: number; originalStatus?: number };

		if (error && error.status === StatusCodeError.Locked) {
			localStorage.removeItem(AppStorage.Token);
			localStorage.removeItem(AppStorage.AccessToken);

			api.dispatch(userActions.logout());
		}

		if (
			error &&
			(error.status === StatusCodeError.Unauthorized || error.originalStatus === StatusCodeError.Unauthorized)
		) {
			const refreshToken = localStorage.getItem(AppStorage.Token);

			if (!refreshPromise) {
				refreshPromise = (async () => {
					const refreshResult = await baseQuery(USER_API_URL)(
						{
							url: 'user/refresh',
							method: 'POST',
							body: isUserApiV2 ? { token: refreshToken } : { token: refreshToken, sector },
						},
						api,
						extraOptions,
					);

					if (!refreshResult.data) {
						return null;
					}

					return refreshResult.data as { refresh_token: string; access_token: string };
				})();
			}

			const currentRefreshPromise = refreshPromise;
			const refreshedTokens = await currentRefreshPromise;
			if (refreshPromise === currentRefreshPromise) {
				refreshPromise = null;
			}

			if (refreshedTokens) {
				const response = refreshedTokens;
				localStorage.setItem(AppStorage.Token, response.refresh_token);
				localStorage.setItem(AppStorage.AccessToken, response.access_token);
				api.dispatch(userActions.setAccessToken(response.access_token));

				return baseQuery(apiUrl)(buildRequestArgs(), api, extraOptions);
			} else {
				api.dispatch(userActions.logout());
				api.dispatch(boatActions.resetBoatById());

				if (!AUTH_V2_ENABLED) {
					await baseQuery(USER_API_URL)({ url: 'grafana/logout', method: 'POST' }, api, extraOptions);
				}
			}
		}
		return result;
	};
